import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  Users,
  Mail,
  FileText
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const ConsentPolicyPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="consent-policy-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Users className="h-8 w-8 text-purple-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Parent/Guardian Consent Policy
              </h1>
              <p className="text-slate-500">Last updated: March 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none">
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12 space-y-8">
              
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  1. Age Requirements
                </h2>
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <p className="text-slate-700 leading-relaxed">
                    <strong>Students under the age of 18</strong> must have a parent or guardian create the account and provide consent for tutoring services.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  2. Registration Requirements
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Parents or guardians must provide the following information during registration for students under 18:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Parent/guardian full name</li>
                  <li>Parent/guardian email address</li>
                  <li>Student's name and date of birth</li>
                  <li>Acknowledgment of consent for tutoring services</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  3. Parent/Guardian Communication
                </h2>
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      For students under 18, parents or guardians will be <strong>copied (CC) on communication emails</strong> including lesson notes and tutoring updates.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  4. Parent/Guardian Responsibilities
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Parents or guardians are responsible for the following when the student is under 18:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Payments:</strong> All payment responsibilities including package purchases and any additional fees</li>
                  <li><strong>Scheduling:</strong> Coordinating tutoring session times and any schedule changes</li>
                  <li><strong>Communication:</strong> Primary point of contact for important updates and notifications</li>
                  <li><strong>Supervision:</strong> Ensuring the student attends scheduled sessions and follows platform rules</li>
                  <li><strong>Consent:</strong> Providing ongoing consent for tutoring services</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  5. Lesson Notes Policy
                </h2>
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 leading-relaxed">
                        After each tutoring session, lesson notes will be sent directly to the student.
                      </p>
                      <p className="text-slate-600 leading-relaxed mt-2">
                        <strong>If the student is under 18</strong>, the parent or guardian will also receive the lesson notes via email. Students can also view lesson notes in their account dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  6. Consent Verification
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors may verify parental consent through:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                  <li>Email confirmation sent to the parent/guardian email address</li>
                  <li>Verification phone calls when necessary</li>
                  <li>Additional documentation requests in specific circumstances</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  7. Withdrawal of Consent
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Parents or guardians may withdraw consent for tutoring services at any time by contacting us at <strong>contact@giftedacademytutors.ca</strong>. Upon withdrawal of consent:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                  <li>The student's account will be deactivated</li>
                  <li>Any remaining unused hours may be refunded subject to our refund policy</li>
                  <li>Access to lesson notes and session history will be terminated</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  8. Privacy and Data Protection
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We take the privacy of minors seriously. All student data is handled in accordance with our Privacy Policy and applicable privacy laws. Parents and guardians have the right to access, correct, or request deletion of their child's personal information.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  9. Contact Information
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  For questions about parent/guardian consent or to update consent information, please contact:
                </p>
                <p className="text-indigo-700 font-medium mt-2">
                  contact@giftedacademytutors.ca
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConsentPolicyPage;
